import React, { useRef, useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface Props {
  value: string;            // '' = All Wallets
  options: string[];
  onChange: (value: string) => void;
}

const BRONZE = '#d4b483';
const HOVER_BG = 'rgba(212,180,131,0.12)';

/**
 * Theme-aware wallet filter dropdown. Replaces the native <select> whose <option>
 * list fell back to unreadable OS defaults. Mirrors the currency selector's custom
 * button + absolute panel pattern so it renders correctly in light and dark mode.
 */
export function WalletFilterSelect({ value, options, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const items = ['', ...options];
  const label = value || 'All Wallets';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs transition-colors"
        style={{
          background: 'rgb(var(--overlay) / 0.03)',
          border: '0.5px solid rgb(var(--overlay) / 0.08)',
          color: 'rgb(var(--sl-100))',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgb(var(--overlay) / 0.06)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgb(var(--overlay) / 0.03)')}
      >
        <span>{label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-sub)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1.5 min-w-[160px] max-h-72 overflow-y-auto rounded-xl shadow-2xl z-50"
          style={{
            background: 'rgb(var(--sl-950))',
            border: '0.5px solid rgb(var(--overlay) / 0.1)',
          }}
        >
          {items.map((opt) => {
            const selected = opt === value;
            const text = opt || 'All Wallets';
            return (
              <button
                key={opt || '__all__'}
                type="button"
                onClick={() => { onChange(opt); setOpen(false); }}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 text-xs transition-colors text-left"
                style={{ color: selected ? BRONZE : 'rgb(var(--sl-100))', fontWeight: selected ? 600 : 400 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = HOVER_BG)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span className="truncate">{text}</span>
                {selected && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRONZE }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
